import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';
import { Building2, Copy, Mail, Link2, Download, FileText } from 'lucide-react';
import { APP_INFO, DURATIONS, generateEnterprise, fill } from '../lib/license';

const LIVE = 'https://rapport-karte.emergent.host/';
const BASES = [...new Set([LIVE, `${window.location.origin}/`])];
export const entLink = (base, code) => `${base}${base.includes('?') ? '&' : '?'}license=${encodeURIComponent(code)}`;

export default function EnterpriseGen({ t, onCreated, copyText }) {
  const [company, setCompany] = useState('');
  const [seats, setSeats] = useState(5);
  const [days, setDays] = useState(365);
  const [base, setBase] = useState(BASES[0]);
  const [out, setOut] = useState(null);
  const until = (u) => new Date(Date.UTC(+u.slice(0, 4), +u.slice(4, 6) - 1, +u.slice(6, 8))).toLocaleDateString(t.locale);

  const gen = async () => {
    if (!company.trim() || !(+seats >= 1)) return toast.error(t.entBad);
    try { setOut(await generateEnterprise(company.trim(), +seats, days, '')); onCreated(); } catch (e) { toast.error(e.message === 'owner' ? t.ownerExpired : t.licOffline); }
  };
  const link = out && entLink(base, out.code);
  const [qr, setQr] = useState(null);
  useEffect(() => { if (link) QRCode.toDataURL(link, { width: 600, margin: 2, errorCorrectionLevel: 'M' }).then(setQr); else setQr(null); }, [link]);
  const fname = (ext) => `GISS-Lizenz-${out.company.replace(/[^\w-]+/g, '_')}.${ext}`;
  const savePng = () => { const a = document.createElement('a'); a.href = qr; a.download = fname('png'); a.click(); };
  const savePdf = () => {
    const d = new jsPDF({ unit: 'mm', format: 'a4' });
    d.setFillColor(10, 37, 64); d.rect(0, 0, 210, 28, 'F');
    d.setTextColor(255); d.setFontSize(18); d.text(APP_INFO.name, 15, 13); d.setFontSize(11); d.text(t.entSheetTitle, 15, 21);
    d.setTextColor(20); d.setFontSize(14); d.text(out.company, 15, 42);
    d.setFontSize(10); d.text(`${out.seats} ${t.entDevices}  |  ${t.colUntil} ${until(out.until)}`, 15, 49);
    d.addImage(qr, 'PNG', 55, 58, 100, 100);
    d.setFontSize(12); d.text(t.entSheetScan, 105, 166, { align: 'center' });
    d.setFontSize(9); d.text(d.splitTextToSize(link, 180), 15, 176);
    d.setFontSize(11); d.text(d.splitTextToSize(t.entSheetSteps.replace('{code}', out.code), 180), 15, 192);
    d.setFontSize(8); d.setTextColor(120); d.text(t.pdfCreated.replace(/–/g, '-'), 15, 287);
    d.save(fname('pdf'));
  };
  const mail = () => {
    const body = fill(t.entMail, { company: out.company, seats: out.seats, date: until(out.until), link, code: out.code, app: APP_INFO.name });
    window.location.href = `mailto:?subject=${encodeURIComponent(`${APP_INFO.name} – ${out.company}`)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="ent-gen" data-testid="enterprise-generator">
      <b><Building2 size={14} /> {t.entTitle}</b>
      <div className="row">
        <input type="text" data-testid="ent-company-input" placeholder={t.entCompany} value={company} onChange={(e) => setCompany(e.target.value)} />
        <input type="number" min="1" data-testid="ent-seats-input" placeholder={t.entSeats} value={seats} onChange={(e) => setSeats(e.target.value)} style={{ maxWidth: 110 }} />
      </div>
      <div className="row">
        <select data-testid="ent-duration-select" value={days} onChange={(e) => setDays(+e.target.value)} aria-label={t.genDuration}>
          {[14, 30, ...DURATIONS].map((d) => <option key={d} value={d}>{`${t.genDuration}: ${t['dur_' + d]}`}</option>)}
        </select>
        <select data-testid="ent-base-select" value={base} onChange={(e) => setBase(e.target.value)} aria-label={t.entBase}>
          {BASES.map((b) => <option key={b} value={b}>{`${t.entBase}: ${b}`}</option>)}
        </select>
      </div>
      <button className="btn btn-proj" data-testid="ent-generate-btn" onClick={gen}><Link2 size={15} /> {t.entBtn}</button>
      {out && (
        <div className="device-box gen-out ent-out" data-testid="ent-result">
          <div>
            <small>{out.company} · {out.seats} {t.entDevices} · {t.colUntil} {until(out.until)}</small>
            <b data-testid="ent-link">{link}</b>
            <small className="mono" data-testid="ent-code">{out.code}</small>
          </div>
          <button className="btn btn-outline btn-sm" data-testid="ent-copy-link-btn" onClick={() => copyText(t, link)}><Copy size={14} /> {t.entCopyLink}</button>
          <button className="btn btn-outline btn-sm" data-testid="ent-mail-btn" onClick={mail}><Mail size={14} /></button>
        </div>
      )}
      {out && qr && (
        <div className="ent-qr" data-testid="ent-qr">
          <img src={qr} alt="QR" data-testid="ent-qr-img" />
          <div className="ent-qr-actions">
            <small>{t.entSheetScan}</small>
            <button className="btn btn-outline btn-sm" data-testid="ent-qr-png-btn" onClick={savePng}><Download size={14} /> {t.entQrPng}</button>
            <button className="btn btn-dark btn-sm" data-testid="ent-qr-pdf-btn" onClick={savePdf}><FileText size={14} /> {t.entQrPdf}</button>
          </div>
        </div>
      )}
    </div>
  );
}
